/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

//TODO removeFields without load
//TODO watch Timeframe inside each async function
//TODO remove this poolId || id crap made for poor Pool's Server only,

import angular from 'angular';

import {
    isNaN,
    pluck,
} from 'underscore';

import { Item } from './item.factory';

/**
 * @constructor
 * @memberOf module:avi/dataModel
 * @extends Item
 * @alias UpdatableItem
 * @param oArgs {object} - Configuration object, contains argument list, event listeners and
 *      more.
 * @desc
 *
 *     Extends {@link Item} by metric data update methods.
 */
class UpdatableItem extends Item {
    constructor(oArgs = {}) {
        super(oArgs);

        const self = this;

        /**
         * @type {angular.$q}
         * @protected
         */
        this.$q_ = this.getAjsDependency_('$q');

        /**
         * @type {module:avi/app.Timeframe}
         * @protected
         */
        this.timeframe_ = this.getAjsDependency_('Timeframe');

        /**
         * @type {AsyncFactory}
         * @protected
         */
        this.AsyncFactory_ = this.getAjsDependency_('AsyncFactory');

        /** @protected */
        this.eventsContext_ = this.getAjsDependency_('eventsContext');

        /**
         * @type {StringService}
         * @protected
         */
        this.stringService_ = this.getAjsDependency_('stringService');

        if (this.id &&
            ['pool', 'serviceengine', 'virtualservice'].indexOf(this.objectName) !== -1) {
            const AlertCollection = this.getAjsDependency_('AlertCollection');

            this.alertsPrioritized = new AlertCollection({
                limit: 15,
                params: {
                    'related_refs.contains': this.poolId || this.id,
                    'event_pages.contains': this.eventsContext_(),
                },
                sortBy: '-timestamp',
            });
        }

        // Creates infrastructure to start and stop all async loops of instance
        this.async = {
            stop(remove) {
                Object.keys(self.async.hash).forEach(key => {
                    self.cancelRequests(key);
                    self.async.hash[key].stop();

                    if (!self.isDestroyed() && key === 'collMetrics') {
                        self.removeMetricData_(
                            _.values(self.async.collMetricsHash),
                        );
                    }

                    if (remove) {
                        //used on controllerChange otherwise we will get
                        // updates after timeframe change even for stopped asyncs
                        if (key === 'collMetrics') {
                            _.each(self.async.collMetricsHash, function(metrics, mName) {
                                metrics.destroy();
                                delete self.async.collMetricsHash[mName];
                            });
                        } else if (!self.isDestroyed() && (key === 'system_events' ||
                            key === 'config_events')) {
                            delete self.data[self.stringService_.snakeToCamelCase(key)];
                        }

                        delete self.async.hash[key];
                    }
                });
            },
            start() {
                Object.keys(self.async.hash).forEach(function(key) {
                    // TODO customizable timeframe for each async
                    const interval = key === 'collMetrics' ?
                        self.collMetricsInterval_ : self.timeframe_.selected().interval * 1000;

                    self.async.hash[key].start(interval);
                });
            },
            hash: {},
            collMetricsHash: {},
        };

        // When timeframe changed restart all async loops in this instance
        this.onTimeframeChange = this.onTimeframeChange.bind(this);

        this.timeframe_.on('change', this.onTimeframeChange);

        this.fields = [];

        const { fields } = oArgs;

        if (fields) {
            if (Array.isArray(fields)) {
                this.fields.push(...fields);
            } else {
                throw new Error('"fields" prop must be an array when set');
            }
        }

        /**
         * Hash of metrics provided by collection this item belongs to. Has nothing to do with
         * Item.async, data is fetched and provided by collection.
         * @type {{string:Metric}}
         */
        this.collMetricsHash = {};
    }

    /**
     * Adds fields to load request
     * @param aFields
     */
    addLoad(aFields) {
        this.fields = _.uniq(this.fields.concat(aFields));

        return this.load();
    }

    /**
     * Removes update channels (fields) from the load chain of {@link Item#loadRequest}.
     * @param {string[]|string} aFields - List of "update chanel" names to be removed. Can be
     *     provided as array or just comma-separated list of names.
     */
    removeLoad(aFields) {
        function isNotString(val) {
            return typeof val !== 'string';
        }

        if (aFields) {
            if (Array.isArray(aFields)) {
                this.fields = _.difference(this.fields, aFields);
            } else if (!_.any(arguments, isNotString)) { //when all are strings
                this.fields = _.difference(this.fields, arguments);
            }
        }
    }

    /**
     * Before calling load remembers the list of fields
     * if called without fields, previous fields will be used
     * @param {*=} aFields
     * @return {*}
     */
    load(aFields) {
        // Replace fields
        if (aFields) {
            this.fields = _.uniq(aFields);
        }

        const loadParams = Array.prototype.slice.call(arguments);

        loadParams[0] = this.fields;

        return super.load(...loadParams);
    }

    /**
     * Loads and puts into {@link Item.data} `config_events`, `system_events` and `alerts`.
     * Used for the performance charts.
     * @param aFields {string[]} - List of fields.
     * @param oParams {Object=} - Call parameters hash to be added into the API URL.
     * @param iInterval {number=} - Interval between calls (milliseconds).
     * @returns {ng.$q.promise}
     */
    loadEventsAndAlerts(aFields, oParams, iInterval) {
        /**
         * Aggregation duration for the events and alerts in seconds. We can show around 30
         * event icons on the chart.
         * @type {{string: number}}
         * @inner
         */
        const stepHash = {
            rt: 60, //up to every minute
            '6h': 15 * 60,
            '1d': 3600, //up to every 1 hour
            '1w': 6 * 3600,
            '1m': 24 * 3600,
            '1q': 7 * 24 * 3600,
            '1y': 2 * 7 * 24 * 3600,
        };

        /**
         * Builds an API URL according to the type and selected timeframe.
         * @param {string} sName - Type of events/alerts we are about to load.
         * @returns {string} - API URL.
         * @inner
         */
        function makeURL(sName) {
            const
                timeframe = self.timeframe_.selected(),
                duration = timeframe.step * timeframe.limit,
                step = stepHash[timeframe.key],
                //number of points for the events API only
                pageSize = Math.floor(duration / step) + 1;

            let url = '';

            switch (sName) {
                case 'system_events':
                    url = `${eventBaseURL}&page_size=${pageSize}&filter=ne(module,CONFIG)`;
                    break;

                case 'config_events':
                    url = `${eventBaseURL}&page_size=${pageSize}&filter=eq(module,CONFIG)`;
                    break;

                case 'alerts':
                    url = alertBaseURL;
                    break;
            }

            url += `&step=${step}&duration=${duration}`;

            if (oParams && !_.isEmpty(oParams)) {
                url += _.reduce(oParams, function(str, val, key) {
                    return `${str + key}=${val}&`;
                }, '&').slice(0, -1);
            }

            return url;
        }

        /**
         * Update function builder to be passed into {@link AsyncFactory}.
         * @param {string} sName - What type of events/alerts do we load now.
         * @param {ng.$q.promise} deferred - Promise to be resolved/rejected on
         * the first update.
         * @returns {Function}
         * @inner
         */
        function load(sName, deferred) {
            //`result` for alerts API, `results` for events API
            const resultsKey = sName === 'alerts' ? 'result' : 'results';

            return function() {
                return self.request('get', makeURL(sName), undefined, null, sName)
                    .then(function(rsp) {
                        self.data[sName] = {
                            series: rsp.data[resultsKey],
                            total: _.reduce(rsp.data[resultsKey], function(memo, item) {
                                return memo + item.value;
                            }, 0),
                        };

                        // For backward compatibility todo: remove when not used
                        self.data[self.stringService_.snakeToCamelCase(sName)] = self.data[sName];
                        deferred.resolve(self.data[sName]);
                    }, function(err) {
                        deferred.reject(err);
                    });
            };
        }

        const self = this;
        const promises = [];
        const sources = ['system_events', 'config_events', 'alerts'];
        const eventBaseURL =
            `/api/analytics/logs?type=2&filter=co(all,"${this.poolId || this.id}")` +
            `&filter=ne(internal,EVENT_INTERNAL)&filter=co(event_pages,${this.eventsContext_()})`;
        const alertBaseURL =
            `/api/alert-count?related_refs.contains=${this.poolId || this.id}` +
            `&event_pages.contains=${this.eventsContext_()}`;

        sources.forEach(function(sName) {
            if (sName in self.async.hash && self.async.hash[sName].isActive()) {
                self.async.hash[sName].stop();
                self.data[sName] = undefined;
                self.data[self.stringService_.snakeToCamelCase(sName)] = undefined;
            }

            if (aFields.indexOf(sName) !== -1) {
                const deferred = self.$q_.defer();

                self.async.hash[sName] = new self.AsyncFactory_(load(sName, deferred), {
                    maxSkipped: 3,
                    callback() {
                        self.cancelRequests(sName);
                    },
                });

                self.async.hash[sName]
                    .start(iInterval || self.timeframe_.selected().interval * 1000);

                promises.push(deferred);
            }
        });

        return this.$q_.all(promises);
    }

    /**
     * Makes GET API call to load metrics data.
     * @param {Object} groupCall
     * @param {number|null} startTime
     * @param {Object=} params
     * @return {ng.$q.promise}
     */
    loadMetricsRequest(groupCall, startTime, params) {
        const { objectName } = this;
        const id = this.poolId || this.id;
        const metricIds = Object.keys(groupCall.metrics.params.metric_id);
        const { step, limit } = groupCall.metrics.params;

        const paramsHash = {
            metric_id: metricIds.join(),
            step,
            ...params,
        };

        if (startTime) {
            const backendDateStringFormat = this.getAjsDependency_('backendDateStringFormat');

            paramsHash.start = moment.utc(startTime).format(backendDateStringFormat);
        } else {
            paramsHash.limit = limit;
        }

        const url = UpdatableItem.getUrl(
            `/api/analytics/metrics/${objectName}/${id}/`,
            paramsHash,
        );

        return this.request('get', url, undefined, null, 'metrics');
    }

    /**
     * This function emulates a call to the server and giving the promise
     * It is making multiple calls periodically to continuously update the object
     * that was delivered into resolve
     *
     * @param aFields {array} - Array of fields that has to be loaded
     * @param oParams {hash} - key: value hash with additional parameters
     * @return {*} - Promise
     */
    loadMetrics(aFields, oParams = undefined, iInterval = undefined) {
        const self = this;
        const deferred = this.$q_.defer();
        const DataTransformers_ = this.getAjsDependency_('DataTransformers');

        // Stop any that were running in previous load
        if (self.async.hash.metrics) {
            self.async.hash.metrics.stop();
            self.cancelRequests('metrics');
        }

        // Run the loop
        // 'metrics' is the hash key as well as the key that must be used to create the requests
        // this enabled them to be easily canceled wyth self.async.stop()
        self.async.hash.metrics = new self.AsyncFactory_(function() {
            // Set up an object to collection information about calls that are nessesary to make
            // in order to load the right data.
            // Each field will be asked to participate to the calls defined in the group
            const groupCall = {
                metrics: {
                    params: {
                        metric_id: {},
                        step: self.timeframe_.selected().step,
                        limit: self.timeframe_.selected().limit,
                        start: null,
                    },
                    response: null,
                    responseAnomalies: null,
                },
                inventory: {
                    include: {},
                    response: null,
                },
            };

            // Preparing to make calls
            // Each field is asked to participate in group call
            _.each(aFields, function(field) {
                if (DataTransformers_[field]) {
                    DataTransformers_[field].beforeCall(self.data, groupCall);
                }
            });

            // Remember step & limit
            self.lastStep = self.timeframe_.selected().step;
            self.lastLimit = self.timeframe_.selected().limit;

            // Group call http requests
            const calls = {};
            let startTime;

            const metricIds = Object.keys(groupCall.metrics.params.metric_id);
            const { step, limit } = groupCall.metrics.params;
            const { objectName } = self;
            const id = self.poolId || self.id;

            // Making metric request if any fields requested to participate in it
            if (metricIds.length) {
                startTime = null;

                if (groupCall.metrics.params.start) {
                    // Always ask for latest know point, this allows backend to smooth out fake
                    // points without making a second db call
                    startTime = groupCall.metrics.params.start;
                }

                calls.metrics = self.loadMetricsRequest(groupCall, startTime, oParams);

                const anomalyApiUrlParams = {
                    metric_id: metricIds.join(),
                    aggregation: 'METRICS_ANOMALY_AGG_COUNT',
                    aggregation_window: 1,
                    step,
                    limit,
                    ...oParams,
                };

                const anomalyApiUrl = UpdatableItem.getUrl(
                    `/api/analytics/anomaly/${objectName}/${id}/`,
                    anomalyApiUrlParams,
                );

                // Calling to get anomalies
                calls.anomalies = self.request(
                    'get',
                    anomalyApiUrl,
                    undefined,
                    null,
                    'metrics',
                );
            }

            const inventoryFields = Object.keys(groupCall.inventory.include);

            // Making inventory call, used to get healthscore and other stuff
            if (inventoryFields.length) {
                const inventoryApiUrlParams = {
                    include_name: true,
                    include: inventoryFields.join(),
                    step,
                    limit,
                };

                let inventoryApiUrlPath = `/api/${objectName}-inventory/${id}/`;

                // pool server item
                if (self.poolId) {
                    inventoryApiUrlPath += 'server/';
                    inventoryApiUrlParams.all_se = true;
                    inventoryApiUrlParams.server = self.id;
                }

                const inventoryApiUrl = UpdatableItem.getUrl(
                    inventoryApiUrlPath,
                    inventoryApiUrlParams,
                );

                calls.inventory = self.request(
                    'get',
                    inventoryApiUrl,
                    undefined,
                    null,
                    'metrics',
                );
            }

            // Waiting and processing responses
            return self.$q_.all(calls).then(function(rsp) {
                // Fullfill group calls with responses
                if (rsp.metrics) {
                    groupCall.metrics.response = rsp.metrics.data;
                }

                if (rsp.anomalies) {
                    groupCall.metrics.responseAnomalies = rsp.anomalies.data;
                }

                if (rsp.inventory) {
                    //for pool's server we get an array with one element
                    if (rsp.inventory.data.results && rsp.inventory.data.results.length) {
                        [groupCall.inventory.response] = rsp.inventory.data.results;
                    } else {
                        groupCall.inventory.response = rsp.inventory.data;
                    }
                }

                // Each field must work with it's data on it's own
                // every field may have it's specific logic and result of it's
                // gonna place in the model's data
                _.each(aFields, function(field) {
                    if (DataTransformers_[field]) {
                        DataTransformers_[field].afterCall(self.data, groupCall);
                    }
                });

                // Triggering event, will be called every time update from server has processed
                // So if you wanna rerender something and you can't watch the data
                // you can bind to the event triggered below
                self.trigger('itemUpdate metricsUpdate', self.data);
                deferred.resolve();
            }, function(rsp) {
                //console.log('Updatable Item "%s" load failed: %O', self.objectName, rsp);
                deferred.reject(rsp);
            });
        }, {
            maxSkipped: 5,
            callback() {
                self.cancelRequests('metrics');
            },
        });

        // Start all the async objects we created
        self.async.hash.metrics.start(iInterval || self.timeframe_.selected().interval * 1000);

        return deferred.promise;
    }

    /**
     * Async updates using collection API. Can be subscribed to and unsubscribed.
     * @param {Object[]} newConfObj
     */
    collMetricsSubscribe(newConfObj) {
        const
            configs = [],
            metricsPromises = [],
            { collMetricsHash } = this.async;

        if (Array.isArray(newConfObj)) {
            configs.push(...newConfObj);
        } else {
            configs.push(newConfObj);
        }

        if (collMetricsHash) {
            const DataTransformers_ = this.getAjsDependency_('DataTransformers');

            configs.forEach(config => {
                if (angular.isObject(config) && config.name) {
                    let metric = collMetricsHash[config.name];

                    const { subscriber, name } = config;

                    if (!metric) {
                        config.item = this;

                        if (subscriber) {
                            config.subscribers = [];
                            config.subscribers.push(subscriber);
                            delete config.subscriber;
                        }

                        if (config.metricClassName) {
                            const $injector = this.getAjsDependency_('$injector');

                            const MetricConstructor = $injector.get(config.metricClassName);

                            metric = new MetricConstructor(config);
                        } else if (name in DataTransformers_ &&
                            angular.isFunction(DataTransformers_[name])) { //modified metric
                            metric = new DataTransformers_[name](config);
                        } else {
                            const CollMetric = this.getAjsDependency_('CollMetric');

                            metric = new CollMetric(config);
                        }

                        collMetricsHash[name] = metric;

                        metricsPromises.push(metric.initialize());
                    } else if (subscriber && !_.contains(metric.subscribers, subscriber)) {
                        metric.subscribers.push(subscriber);
                    }
                }
            });
        }

        return this.$q_.all(metricsPromises);
    }

    /**
     * Destroys and removes metrics from asyncCollHash.
     * @param {Object.<string, string>|Array<Object.<string, string>>|string} confList - Name of
     *     metric names to unsubscribe from with the name of Subscriber which is optional.
     *     Can be one object or array or name of subscriber to ubsubscribe from all metrics it
     *     owns.
     */
    collMetricsUnSubscribe(confList) {
        const
            list = [],
            { collMetricsHash } = this.async;

        if (!collMetricsHash) {
            return;
        }

        if (Array.isArray(confList)) {
            list.push(...confList);
        } else if (angular.isObject(confList) && 'name' in confList) {
            list.push(confList);
        } else if (angular.isString(confList)) {
            const subscriber = confList; //name of subscriber

            _.each(collMetricsHash, metric => {
                if (_.contains(metric.subscribers, subscriber)) {
                    list.push({
                        name: metric.getId(),
                        subscriber,
                    });
                }
            });
        }

        list.forEach(remConf => {
            if (angular.isObject(remConf) &&
                'name' in remConf &&
                remConf.name in collMetricsHash
            ) {
                const metric = collMetricsHash[remConf.name];

                let toDrop = !remConf.subscriber;

                if (!toDrop) { //have 'subscriber' passed
                    const
                        { subscribers } = metric,
                        subscriberIndex = subscribers.indexOf(remConf.subscriber);

                    if (subscriberIndex !== -1) {
                        if (subscribers.length > 1) {
                            metric.subscribers.splice(subscriberIndex, 1);
                        } else { //just one subscriber
                            toDrop = true;
                        }
                    }
                }

                if (toDrop) {
                    this.removeMetricData_(metric);
                    metric.destroy();
                    delete collMetricsHash[remConf.name];
                }
            }
        });
    }

    /**
     * Returns Metric instance by its name.
     * @param {string} mName
     * @returns {Metric|null}
     * @private
     */
    metricLookup_(mName) {
        return this.async.collMetricsHash[mName] || this.collMetricsHash[mName] || null;
    }

    /**
     * Returns metric instance(s) being fetched by this Item.
     * @param {string[]} mNames - Unique within Item.async.collMetricsHash metrics id/name.
     * @returns {Metric|Metric[]|null} - Single value is returned when string was passed as an
     *     attribute. Array otherwise.
     * @public
     */
    getMetric(mNames) {
        if (angular.isString(mNames)) {
            return this.metricLookup_(mNames);
        }

        return mNames.map(mName => this.metricLookup_(mName));
    }

    //TODO add ability for immediate updates
    /**
     * @return {Promise<void>}
     */
    startCollMetricsAsync() {
        const {
            AsyncFactory_: AsyncFactory,
            $q_: $q,
        } = this;

        const {
            hash: asyncHash,
            collMetricsHash,
        } = this.async;

        const deferred = $q.defer();

        // Stop any that were running in previous load
        if (asyncHash.collMetrics) {
            asyncHash.collMetrics.stop();
            this.cancelRequests('collMetrics');
        }

        const collMetricsAsyncFactory = new AsyncFactory(() => {
            // arrays of request objects for every metric and corresponding anomalies
            const requests = [];
            const aRequests = [];

            Object.values(collMetricsHash)
                .forEach(metric => {
                    const request = metric.beforeCall();

                    if (request) {
                        requests.push(request.series);
                        aRequests.push(request.anomalies);
                    }
                });

            if (!requests.length) {
                return $q.when();
            }

            const calls = [];

            const metricsApiUrlPath = '/api/analytics/metrics/collection/';

            const metricsApiUrlParams = {
                include_name: true,
                include_refs: true,
                pad_missing_data: false,
                dimension_limit: 1000,
            };

            const metricsApiUrl = UpdatableItem.getUrl(metricsApiUrlPath, metricsApiUrlParams);

            const metricsApiCall = this.request(
                'post',
                metricsApiUrl,
                { metric_requests: requests },
                undefined,
                'collMetrics',
            );

            calls.push(metricsApiCall);

            const { objectName } = this;
            const id = this.poolId || this.id;

            const anomalyApiUrlPath = `/api/analytics/anomaly/${objectName}/${id}/`;
            const anomalySeries = pluck(aRequests, 'series');
            const { step, limit } = this.timeframe_.selected();

            const anomalyApiUrlParams = {
                metric_id: anomalySeries.join(),
                aggregation: 'METRICS_ANOMALY_AGG_COUNT',
                aggregation_window: 1,
                step,
                limit,
            };

            const anomalyRequestStartTimestamp =
                UpdatableItem.getMinCollMetricsRequestStartTime_(aRequests);

            if (!isNaN(anomalyRequestStartTimestamp)) {
                const backendDateStringFormat = this.getAjsDependency_('backendDateStringFormat');

                anomalyApiUrlParams.start =
                    moment.utc(anomalyRequestStartTimestamp)
                        .format(backendDateStringFormat);
            }

            // pool server case
            if (this.poolId) {
                anomalyApiUrlParams.server = this.id;
            }

            const anomalyApiUrl = UpdatableItem.getUrl(anomalyApiUrlPath, anomalyApiUrlParams);

            const anomalyApiRequest = this.request(
                'get',
                anomalyApiUrl,
                undefined,
                null,
                'collMetrics',
            );

            calls.push(anomalyApiRequest);

            return $q.all(calls)
                .then(
                    ([
                        { data: { series: metricSeries } },
                        { data: { series: anomalySeries } },
                    ]) => {
                        Object.values(collMetricsHash)
                            .forEach(metric => {
                                metric.afterCall(metricSeries, anomalySeries);
                                this.saveMetricData_(metric);
                            });

                        this.trigger('itemUpdate metricsUpdate', this.data);

                        deferred.resolve();
                    },
                ).catch(rsp => deferred.reject(rsp));
        }, {
            maxSkipped: 5,
            callback: () => {
                this.cancelRequests('collMetrics');
            },
        });

        // kicking updates off
        collMetricsAsyncFactory.start(this.collMetricsInterval_);

        asyncHash.collMetrics = collMetricsAsyncFactory;

        return deferred.promise;
    }

    /**
     * Returns an Array of config objects to be used by chartConfig service
     * Should be called after subscribing to collMetrics updates.
     * Doesn't include (or works with) GET analytics calls.
     * If list is not provided, will return configs for all metrics.
     * @param list {Array|string=} list of metrics names which should be inside
     * collMetricsHash.
     */
    //TODO drop
    createChartsConfig(list) {
        const { collMetricsHash: hash } = this.async;

        let filteredList;

        if (!list) {
            filteredList = Object.keys(hash);
        } else {
            if (angular.isString(list)) {
                list = [list];
            }

            filteredList = _.filter(list, mName => mName in hash);
        }

        return _.reduce(filteredList, (acc, mName) => {
            const
                metric = hash[mName],
                mSeries = metric.getSeriesByType('regular');

            const errorTotalSeries = metric.getSeriesByType('errorTotal');

            const cardConfig = {
                id: mName,
                series: mSeries.map(series => series.getId()),
                errorSeries:
                    metric.getSeriesByType('error')
                        .map(series => series.getId()),
                errorTotal: errorTotalSeries ? errorTotalSeries.getId() : '',
                type: metric.type, //for scatterPlot for ex
                summaryTitle: metric.getTitle(),
                metric,
            };

            const totalSeries = metric.getSeriesByType('total');

            if (totalSeries) {
                cardConfig.totalSeries = totalSeries.getId();
            }

            acc.push(cardConfig);

            return acc;
        }, []);
    }

    /**
     * Since all Collection Metrics subrequests have to provide a unique subrequestId,
     * every Item which participates in such updates as a Collection Item should provide
     * a unique within whole collection id for this purpose.
     * @returns {string} - unique id of subrequest for Collection Metrics API when updates are
     * organized by Collection.
     */
    getGroupCollMetricsRequestId() {
        return `collItemRequest:${this.id}`;
    }

    /**
     * When Item is a part of Collection we need to filter out some dataFields
     * (since one list of dataFields list is used throughout Collection instance) which
     * are not applicable for this particular Item due to its configuration state.
     * @param {string} fieldName - dataField name to check.
     * @returns {boolean} - True if applicable, false otherwise.
     * @public
     * @abstract
     */
    dataFieldIsApplicable(fieldName) { return true; }

    /**
     * Used by collection when it is responsible for group updates through the collection metric
     * API. Not used by lone Item.
     * @param {string[]} fields - Metric names.
     * @returns {CollMetricsRequest[]} - Requests array to be included into group collection
     * metrics call to be executed by Collection.
     */
    //TODO support unusual metrics
    //TODO remove unused metrics
    //TODO workaround, subscribe/unsubscribe methods are needed
    getCollMetricsRequests(fields) {
        const requests = [];

        if (Array.isArray(fields)) {
            _.each(fields, function(fieldName) {
                if (this.dataFieldIsApplicable(fieldName)) {
                    if (!(fieldName in this.collMetricsHash)) {
                        const GroupCollMetric = this.getAjsDependency_('GroupCollMetric');

                        this.collMetricsHash[fieldName] = new GroupCollMetric({
                            name: fieldName,
                            item: this,
                            subscribers: ['GroupCollMetricRequest'],
                            series: fieldName,
                        });
                    }

                    const request = this.collMetricsHash[fieldName].beforeCall();

                    if (request) {
                        requests.push(request.series);
                    }
                }
            }, this);
        }

        return requests;
    }

    /**
     * Used by Collection to deliver updates into Items which participate in a group collection
     * metrics call. Not used by lone Item.
     * @param {CollMetricsFullResponse} resp
     */
    processCollMetricsResponse(resp) {
        _.each(this.collMetricsHash, metric => {
            metric.afterCall(resp);
            this.saveMetricData_(metric);
        });
    }

    /**
     * Loads health score time series. No repeated updates, just once.
     * @return {ng.$q.promise}
     * @public
     */
    loadHsGlance() {
        const { data } = this;

        if (!data.hsSeriesMetric) {
            const HealthScoreMetric = this.getAjsDependency_('HealthScoreMetric');

            data.hsSeriesMetric = new HealthScoreMetric({ item: this });
            data.hsSeries = data.hsSeriesMetric.getMainSeries();
        }

        const
            { hsSeriesMetric } = data,
            itemIdForApi = this.poolId || this.id;

        let requestConfig = hsSeriesMetric.beforeCall();

        if (!requestConfig) {
            // no call is needed since data we have is fresh enough
            return this.$q_.when(true);
        }

        requestConfig = requestConfig.series;

        let url =
            `/api/analytics/healthscore/${this.objectName}/${itemIdForApi}` +
            `?summary=true&step=${requestConfig.step}&limit=${requestConfig.limit}`;

        if (requestConfig.start) {
            url += `&start=${moment.utc(requestConfig.start).toISOString()}`;
        }

        return this.request('get', url, undefined, null, 'loadHsGlance')
            .then(({ data }) => {
                hsSeriesMetric.afterCall(data);
            });
    }

    /**
     * Function passed to Timeframe's change event.
     * Will be redefined inside each Item as func.bind(this) by Constructor
     */
    onTimeframeChange() {
        this.async.stop();
        this.async.start();
    }

    /** @override */
    //TODO destroy data.hsSeriesMetric
    destroy() {
        const gotDestroyed = super.destroy();

        if (gotDestroyed) {
            this.timeframe_.unbind('change', this.onTimeframeChange);
            this.async.stop(true);

            _.each(this.collMetricsHash, metric => metric.destroy());
            this.collMetricsHash = null;
        }

        return gotDestroyed;
    }

    /**
     * Removes metric series data from Item.data.
     * @param {Metric[]|Metric} metricList
     * @protected
     */
    removeMetricData_(metricList) {
        if (!metricList) {
            return;
        }

        if (!Array.isArray(metricList)) {
            metricList = [metricList];
        }

        const { data } = this;

        metricList.forEach(metric => {
            metric.getSeries().forEach(
                series => delete data[series.getId()],
            );

            if (metric.totalSeries) {
                delete data[metric.totalSeries.getId()];
            }
        });
    }

    /**
     * Saves metric series data into Item.data.
     * @param {Metric[]|Metric} metricList
     * @protected
     */
    // FIXME we need to keep metrics somewhere else and never store flat series hash
    saveMetricData_(metricList) {
        if (!metricList) {
            return;
        }

        if (!Array.isArray(metricList)) {
            metricList = [metricList];
        }

        const { data } = this;

        metricList.forEach(metric => {
            metric.getSeries().forEach(
                series => data[series.getId()] = series,
            );

            if (metric.totalSeries) {
                const { totalSeries } = metric;

                data[totalSeries.getId()] = totalSeries;
            }
        });
    }

    /**
     * Constructs API url from path and params hash.
     * @param {string} path
     * @param {Object=} params - Hash of key & value pairs.
     * @return {string}
     * @static
     */
    static getUrl(path, params) {
        const queryParamsStr = _.reduce(params,
            (acc, value, key) => `${acc}${key}=${value}&`,
            '');

        if (!queryParamsStr) {
            return path;
        }

        return `${path}?${queryParamsStr}`;
    }

    /**
     * Returns the earliest "start" timestamp among collection metrics requests passed.
     * Timestamp is returned as number in "unix" format (with ms).
     * @param {Object[]} requests - Has optional "start" property in the form of a string.
     * @returns {number} - returns NaN when one (or more) of requests passed didn't have
     *     "start" set.
     * @protected
     */
    static getMinCollMetricsRequestStartTime_(requests = []) {
        let startTimestamp = +Infinity;
        let everyRequestHasStart = false;

        // eslint-disable-next-line no-restricted-syntax
        for (const request of requests) {
            const { start: startStr } = request;

            if (!startStr) {
                everyRequestHasStart = false;
                break;
            }

            everyRequestHasStart = true;

            const timestamp = +moment(startStr);

            startTimestamp = Math.min(startTimestamp, timestamp);
        }

        if (!everyRequestHasStart) {
            return NaN;
        }

        return startTimestamp;
    }
}

/**
 * CollMetrics async function will always start every set number of milliseconds.
 * Every metric inside this function makes a decision if it should
 * take part in this update. Hence they can have different frequency of updates.
 * @type {number}
 * @protected
 */
UpdatableItem.prototype.collMetricsInterval_ = 9990;

/**
 * Keeps the list of requested fields
 * @type {Array}
 */
UpdatableItem.prototype.fields = [];

/**
 * Returns reference to the angular dependency by it's name.
 * @param {string} name
 * @return {*|null}
 * @protected
 * @abstract
 */
UpdatableItem.prototype.getAjsDependency_ = function(name) {
    return null;
};

UpdatableItem.ajsDependencies = [
    '$q',
    '$injector',
    'Timeframe',
    'AlertCollection',
    'AsyncFactory',
    'DataTransformers',
    'eventsContext',
    'CollMetric',
    'GroupCollMetric',
    'HealthScoreMetric',
    'backendDateStringFormat',
    'stringService',
];

export { UpdatableItem };
